import type { TextFieldProps } from "@mui/material";
import { TextField } from "@mui/material";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

type ErrorInfo = {
  visible: boolean;
  // If groupId is included, then all current errors with the same groupId
  // must be validated before any of them will be made visible. However, once
  // an error has been made visible, it will remain visible even if future
  // errors with the same groupId are added. For now the main use for this
  // feature is management of "duplicate value" errors across several fields.
  groupId?: string;
};

type ValidationState = Map<string, ErrorInfo>;

type ValidationContextValue = {
  errors: ValidationState;
  setErrorInfo: (field: string, errorInfo: ErrorInfo | null) => void;
  validateFields: (fields: string[]) => void;
};

/*
  Important note that the ValidationContext is meant to be nested inside of
  itself for tracking errors at multiple levels in the form. Currently we
  have a ValidationContext.Provider rendered at the top form level and also
  at each step of the form, using the value returned from
  useValidationContextValue(). This allows fields to report errors and their
  visibility to the step context, and those changes are all propagated to the
  top-level form context. The top level context is also able to trigger
  error visibility and propagate that downward.

  What we get from this:
  - Errors in fields the user hasn't yet interacted with can be kept silent
  (usually these are errors for empty values)
  - Steps can display a notification when there are visible errors inside
  - Steps, on closing, can force visibility on any contained errors
  - The form, on submit, can force visibility on all errors
*/
export const ValidationContext = createContext({} as ValidationContextValue);

type ValidationAction =
  | {
      type: "SET_ERROR_INFO";
      field: string;
      errorInfo: ErrorInfo | null;
    }
  | { type: "VALIDATE_FIELDS"; fields: string[] }
  | { type: "VALIDATE_ALL_FIELDS_IN_SECTION" };

function validationReducer(
  errors: ValidationState,
  action: ValidationAction,
): ValidationState {
  const newErrors = new Map(errors);
  let somethingChanged = false;
  switch (action.type) {
    case "SET_ERROR_INFO":
      somethingChanged = true;
      if (action.errorInfo) {
        newErrors.set(action.field, action.errorInfo);
      } else {
        newErrors.delete(action.field);
      }
      break;
    case "VALIDATE_FIELDS":
      for (const [field, errorInfo] of newErrors) {
        if (!errorInfo.visible && action.fields.includes(field)) {
          somethingChanged = true;
          newErrors.set(field, { ...errorInfo, visible: true });
        }
      }
      break;
    case "VALIDATE_ALL_FIELDS_IN_SECTION":
      // we'll guarantee a React update in the case that this action is called
      somethingChanged = true;
      for (const [field, errorInfo] of newErrors) {
        if (!errorInfo.visible) {
          newErrors.set(field, { ...errorInfo, visible: true });
        }
      }
      break;
  }
  // prevent infinite feedback loop between upstream context and step context
  return somethingChanged ? newErrors : errors;
}

// This returns a new context value that syncs with an upstream
// ValidationContext, meant to be passed into a ValidationContext.Provider.
// It is not meant for consuming ValidationContext at the field level.
export function useValidationContextValue(): {
  validationContextValue: ValidationContextValue;
  validateAllFieldsInSection: () => void;
} {
  const [internalErrors, dispatchValidationAction] = useReducer(
    validationReducer,
    new Map(),
  );
  const [publicErrors, setPublicErrors] = useState<ValidationState>(new Map());
  // useLayoutEffect ensures that downward propagation effects take place
  // before next animation frame, which is needed for querying/scrolling to
  // errored steps on form submit.
  useLayoutEffect(() => {
    setPublicErrors((publicErrors) => {
      const newErrors: ValidationState = new Map();
      for (const [field, errorInfo] of internalErrors) {
        const { groupId } = errorInfo;
        if (groupId && !publicErrors.get(field)?.visible) {
          newErrors.set(field, {
            visible: [...internalErrors.values()]
              .filter((err) => err.groupId === groupId)
              .every((err) => err.visible),
            groupId,
          });
        } else {
          newErrors.set(field, errorInfo);
        }
      }
      return newErrors;
    });
  }, [internalErrors]);
  const {
    errors: upstreamErrors,
    setErrorInfo: upstreamSetErrorInfo,
    validateFields: upstreamValidateFields,
  }: Partial<ValidationContextValue> = useContext(ValidationContext);
  const setErrorInfo: ValidationContextValue["setErrorInfo"] = useCallback(
    (field, errorInfo) => {
      dispatchValidationAction({
        type: "SET_ERROR_INFO",
        field,
        errorInfo,
      });
      if (upstreamSetErrorInfo) {
        upstreamSetErrorInfo(field, errorInfo);
      }
    },
    [upstreamSetErrorInfo],
  );
  const validateFields: ValidationContextValue["validateFields"] = useCallback(
    (fields) => {
      dispatchValidationAction({
        type: "VALIDATE_FIELDS",
        fields,
      });
    },
    [],
  );
  useEffect(() => {
    if (upstreamValidateFields) {
      upstreamValidateFields(
        [...publicErrors]
          .filter(([, { visible }]) => visible)
          .map(([field]) => field),
      );
    }
  }, [publicErrors, upstreamValidateFields]);
  // useLayoutEffect for fast downward propagation (see useLayoutEffect above
  // for setPublicErrors)
  useLayoutEffect(() => {
    if (upstreamErrors) {
      validateFields(
        [...upstreamErrors]
          .filter(([, { visible }]) => visible)
          .map(([field]) => field),
      );
    }
  }, [upstreamErrors, validateFields]);
  const validationContextValue = useMemo(
    () => ({
      errors: publicErrors,
      setErrorInfo,
      validateFields,
    }),
    [publicErrors, setErrorInfo, validateFields],
  );
  const validateAllFieldsInSection = useCallback(() => {
    dispatchValidationAction({
      type: "VALIDATE_ALL_FIELDS_IN_SECTION",
    });
  }, []);
  return {
    validationContextValue,
    validateAllFieldsInSection,
  };
}

type ValidatedFieldError =
  | null
  | string
  | {
      message: string;
      groupId?: string;
    };

/**
 * Registers a non-TextField-based field (e.g. a ResourceSelector) into the
 * ValidationContext, mirroring ValidationTextField's own registration so both
 * kinds of fields participate identically in submit-time forced visibility
 * and cross-field groupId checks (e.g. duplicate-value detection).
 *
 * Wire `handleFocus`/`handleBlur` to a wrapping element's onFocus/onBlur —
 * React's synthetic focus/blur bubble via focusin/focusout, so a wrapper div
 * around the field reliably detects focus entering/leaving it, the same way
 * TextField's own onFocus/onBlur would.
 */
export function useValidatedResourceField({
  instanceId,
  valueForValidation,
  error,
  validateOthersOnBlur,
}: {
  instanceId: string;
  valueForValidation: string | number;
  error: ValidatedFieldError;
  validateOthersOnBlur?: string[];
}) {
  const {
    errors: contextErrors,
    setErrorInfo,
    validateFields,
  } = useContext(ValidationContext);
  const focused = useRef(false);

  const errorMessage =
    error && (typeof error === "object" ? error.message : error);
  const groupId =
    error && typeof error === "object" ? error.groupId : undefined;
  const hasValue = Boolean(String(valueForValidation));

  useEffect(() => {
    setErrorInfo(
      instanceId,
      errorMessage
        ? {
            visible: hasValue && !focused.current,
            groupId,
          }
        : null,
    );
  }, [instanceId, hasValue, errorMessage, groupId, setErrorInfo]);

  useEffect(() => {
    return () => {
      setErrorInfo(instanceId, null);
    };
    // eslint-disable-next-line
  }, []);

  const handleFocus = useCallback(() => {
    focused.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    focused.current = false;
    const fieldsToValidate = validateOthersOnBlur
      ? [instanceId, ...validateOthersOnBlur]
      : [instanceId];
    validateFields(fieldsToValidate);
  }, [instanceId, validateOthersOnBlur, validateFields]);

  const visibleErrorMessage =
    errorMessage && contextErrors.get(instanceId)?.visible
      ? errorMessage
      : null;

  return { visibleErrorMessage, handleFocus, handleBlur };
}

type Props = Omit<TextFieldProps, "error"> & {
  // instanceId is a unique string that should be consistent for the duration
  // the ValidationTextField is mounted by React. For single fields it can be
  // a hardcoded string. For multi-field lists, it should be created as a
  // function of the value of the `key` prop passed into the corresponding
  // list element.
  // (Note: we used to create this ID using a React ref on the component but
  // passing a string was simpler overall)
  instanceId: string;
  valueForValidation: string | number;
  error:
    | null
    | string
    | {
        message: string;
        groupId?: string;
      };
  validateOthersOnBlur?: string[];
};

type UnRef<T> = T extends (instance: infer U | null) => void
  ? U
  : T extends React.RefObject<infer U>
    ? U
    : never;
type TextFieldRef = UnRef<TextFieldProps["ref"]>;

export const ValidationTextField = React.forwardRef<TextFieldRef, Props>(
  (props: Props, ref) => {
    const focused = useRef(false);
    const {
      errors: contextErrors,
      setErrorInfo,
      validateFields,
    } = useContext(ValidationContext);

    const {
      instanceId,
      valueForValidation,
      error,
      onFocus,
      onBlur,
      validateOthersOnBlur,
      ...rest
    } = props;

    const errorMessage =
      error && (typeof error === "object" ? error.message : error);
    const groupId =
      error && typeof error === "object" ? error.groupId : undefined;
    const hasValue = Boolean(String(valueForValidation));
    useEffect(() => {
      setErrorInfo(
        instanceId,
        errorMessage
          ? {
              visible: hasValue && !focused.current,
              groupId,
            }
          : null,
      );
    }, [instanceId, hasValue, errorMessage, groupId, setErrorInfo]);
    useEffect(() => {
      return () => {
        setErrorInfo(instanceId, null);
      };
      // eslint-disable-next-line
    }, []);

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        focused.current = true;
        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus],
    );

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        focused.current = false;
        let fieldsToValidate: string[] = [instanceId];
        if (validateOthersOnBlur) {
          fieldsToValidate = fieldsToValidate.concat(validateOthersOnBlur);
        }
        validateFields(fieldsToValidate);
        if (onBlur) {
          onBlur(e);
        }
      },
      [instanceId, validateFields, validateOthersOnBlur, onBlur],
    );

    const extraProps =
      error && contextErrors.get(instanceId)?.visible
        ? {
            error: true,
            helperText: errorMessage,
          }
        : {};

    const inputLabelProps = {
      sx: { fontSize: "1rem", ...props.InputLabelProps?.sx },
      ...props.InputLabelProps,
    };

    return (
      <TextField
        {...rest}
        {...extraProps}
        ref={ref}
        onFocus={handleFocus}
        onBlur={handleBlur}
        InputLabelProps={inputLabelProps}
      />
    );
  },
);

ValidationTextField.displayName = "ValidationTextField";
